import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BetweenHorizontalEnd, BetweenHorizontalStart, BetweenVerticalEnd, BetweenVerticalStart, Columns3, MessageSquarePlus, Rows3, Send, Table, TableCellsMerge, TableCellsSplit, Trash2 } from 'lucide-react';
import { CellSelection, TableMap, selectionCell } from '@tiptap/pm/tables';
import { toast } from 'sonner';
import { serializeCellSelection, serializeTable } from '../../lib/tableSerializer';

const TOOLBAR_FALLBACK_WIDTH = 240;
// T-138: hueco entre el toolbar flotante y la tabla (arriba/abajo). Bajado de
// 12 a 4 para que quede pegado a la tabla (pedido del usuario: estaba muy lejos).
const TOOLBAR_VERTICAL_OFFSET = 4;
const TOOLBAR_FALLBACK_HEIGHT = 36;

const getScrollParents = (element) => {
  const scrollParents = [];
  let current = element?.parentElement;

  while (current) {
    const styles = window.getComputedStyle(current);
    const overflowValue = `${styles.overflow}${styles.overflowX}${styles.overflowY}`;

    if (/(auto|scroll|overlay)/.test(overflowValue)) {
      scrollParents.push(current);
    }

    current = current.parentElement;
  }

  return scrollParents;
};

const getActiveTableElement = (editor) => {
  if (!editor?.isActive('table')) {
    return null;
  }

  const { selection } = editor.state;
  const anchor = selection.$anchorCell || selection.$from;

  for (let depth = anchor.depth; depth > 0; depth -= 1) {
    if (anchor.node(depth).type.name !== 'table') {
      continue;
    }

    const tablePos = anchor.before(depth);
    const domNode = editor.view.nodeDOM(tablePos);

    if (!(domNode instanceof HTMLElement)) {
      return null;
    }

    if (domNode.matches('table')) {
      return domNode;
    }

    return domNode.querySelector('table') || domNode;
  }

  return null;
};

// The active table's ProseMirror node (to read its comment attribute).
const getActiveTableNode = (editor) => {
  if (!editor?.isActive('table')) return null;
  const { selection } = editor.state;
  const anchor = selection.$anchorCell || selection.$from;
  for (let depth = anchor.depth; depth > 0; depth -= 1) {
    if (anchor.node(depth).type.name === 'table') return anchor.node(depth);
  }
  return null;
};

// Whole-table comment id from the table node's commentId attribute (null when
// empty/resolved). Drives the amber "this table is commented" button state.
const getTableCommentId = (tableNode) => {
  if (!tableNode) return null;
  return tableNode.attrs.commentId && !tableNode.attrs.commentResolved
    ? tableNode.attrs.commentId
    : null;
};

const TableFloatingToolbar = ({ editor }) => {
  const toolbarRef = useRef(null);
  const rafRef = useRef(null);
  const [position, setPosition] = useState({ visible: false, top: 0, left: 0 });

  const hideToolbar = useCallback(() => {
    setPosition((previous) => (previous.visible ? { ...previous, visible: false } : previous));
  }, []);

  const updateToolbarPosition = useCallback(() => {
    if (!editor) {
      hideToolbar();
      return;
    }

    const tableElement = getActiveTableElement(editor);
    const editorWrapper = editor.view.dom.closest('[data-testid="tiptap-editor"]');

    if (!tableElement || !editorWrapper) {
      hideToolbar();
      return;
    }

    const wrapperRect = editorWrapper.getBoundingClientRect();
    const tableRect = tableElement.getBoundingClientRect();
    const toolbarWidth = toolbarRef.current?.offsetWidth || TOOLBAR_FALLBACK_WIDTH;
    const toolbarHeight = toolbarRef.current?.offsetHeight || TOOLBAR_FALLBACK_HEIGHT;
    const maxLeft = Math.max(wrapperRect.width - toolbarWidth, 0);
    const nextLeft = Math.min(Math.max(tableRect.left - wrapperRect.left, 0), maxLeft);
    const preferredTop = tableRect.top - wrapperRect.top - toolbarHeight - TOOLBAR_VERTICAL_OFFSET;
    const nextTop = preferredTop >= 0
      ? preferredTop
      : tableRect.bottom - wrapperRect.top + TOOLBAR_VERTICAL_OFFSET;

    const tableTop = tableRect.top - wrapperRect.top;
    const tableRight = tableRect.right - wrapperRect.left;
    const commentId = getTableCommentId(getActiveTableNode(editor));

    setPosition((previous) => {
      if (
        previous.visible
        && previous.top === nextTop
        && previous.left === nextLeft
        && previous.tableTop === tableTop
        && previous.tableRight === tableRight
        && previous.commentId === commentId
      ) {
        return previous;
      }

      return { visible: true, top: nextTop, left: nextLeft, tableTop, tableRight, commentId };
    });
  }, [editor, hideToolbar]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateToolbarPosition();
    });
  }, [updateToolbarPosition]);

  useEffect(() => {
    if (!editor) {
      return undefined;
    }

    const handleBlur = () => hideToolbar();
    const scrollParents = getScrollParents(editor.view.dom);

    scheduleUpdate();
    editor.on('selectionUpdate', scheduleUpdate);
    editor.on('update', scheduleUpdate);
    editor.on('focus', scheduleUpdate);
    editor.on('blur', handleBlur);
    window.addEventListener('resize', scheduleUpdate);
    scrollParents.forEach((parent) => parent.addEventListener('scroll', scheduleUpdate, { passive: true }));

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      editor.off('selectionUpdate', scheduleUpdate);
      editor.off('update', scheduleUpdate);
      editor.off('focus', scheduleUpdate);
      editor.off('blur', handleBlur);
      window.removeEventListener('resize', scheduleUpdate);
      scrollParents.forEach((parent) => parent.removeEventListener('scroll', scheduleUpdate));
    };
  }, [editor, hideToolbar, scheduleUpdate]);

  useEffect(() => {
    if (position.visible) {
      scheduleUpdate();
    }
  }, [position.visible, scheduleUpdate]);

  if (!editor || !position.visible) {
    return null;
  }

  const handleMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const runCommand = (command) => {
    command();
    scheduleUpdate();
  };

  // Reason (T-013 / R1): select the current row/column/whole table as a native
  // prosemirror-tables CellSelection so the user can then act on that rectangle
  // (visual highlight + "Enviar al chat"). Drag-rectangle selection already
  // works natively; these buttons cover the keyboard/precision path.
  const setCellSelection = (build) => {
    const { state, view } = editor;
    try {
      const $cell = selectionCell(state);
      const selection = build($cell, state);
      if (!selection) return;
      view.dispatch(state.tr.setSelection(selection));
      view.focus();
    } catch (error) {
      console.warn('Table selection command failed:', error);
    }
  };

  const selectRow = () => setCellSelection(($cell) => CellSelection.rowSelection($cell));
  const selectColumn = () => setCellSelection(($cell) => CellSelection.colSelection($cell));
  const selectWholeTable = () => setCellSelection(($cell, state) => {
    const table = $cell.node(-1);
    const tableStart = $cell.start(-1);
    const map = TableMap.get(table);
    if (!map.map.length) return null;
    const anchorPos = tableStart + map.map[0];
    const headPos = tableStart + map.map[map.map.length - 1];
    return CellSelection.create(state.doc, anchorPos, headPos);
  });

  // Reason (T-013 / R1): send the selected table fragment (or the whole table
  // when there is only a text cursor) to the chat via the existing `textSelected`
  // window event — the same channel the document-selection path uses, so the
  // ChatSidebar's ContextSelector reflects it as an attached "selection" chip.
  // Per CONTRATO v3 the AI gets GFM, falling back to HTML for merged cells.
  const sendTableToChat = () => {
    const { state } = editor;
    let result = serializeCellSelection(state);

    if (!result) {
      // Only a text cursor is in the table — serialize the whole table node.
      try {
        const $cell = selectionCell(state);
        result = serializeTable($cell.node(-1));
      } catch (error) {
        console.warn('Could not resolve table for chat context:', error);
      }
    }

    if (!result || (!result.gfm && !result.html)) {
      toast.error('No se pudo preparar la tabla para el chat.');
      return;
    }

    // hasMerges → HTML channel (has_tables=true); otherwise GFM plaintext channel.
    const useHtml = result.hasMerges;
    window.dispatchEvent(new CustomEvent('textSelected', {
      detail: {
        text: result.gfm || result.html || '',
        images: [],
        html: useHtml ? result.html : '',
        hasTables: useHtml,
      },
    }));

    toast.success('Fragmento de tabla enviado al chat.');
  };

  const buttons = [
    // Columnas: insertar izquierda / derecha / eliminar
    {
      key: 'add-column-before',
      icon: BetweenVerticalStart,
      label: 'Insertar columna a la izquierda',
      disabled: !editor.can().addColumnBefore(),
      onClick: () => runCommand(() => editor.chain().focus().addColumnBefore().run()),
    },
    {
      key: 'add-column',
      icon: BetweenVerticalEnd,
      label: 'Insertar columna a la derecha',
      disabled: !editor.can().addColumnAfter(),
      onClick: () => runCommand(() => editor.chain().focus().addColumnAfter().run()),
    },
    {
      key: 'delete-column',
      icon: Trash2,
      label: 'Eliminar columna',
      disabled: !editor.can().deleteColumn(),
      onClick: () => runCommand(() => editor.chain().focus().deleteColumn().run()),
    },
    { key: 'separator-1', separator: true },
    // Renglones: insertar arriba / abajo / eliminar
    {
      key: 'add-row-before',
      icon: BetweenHorizontalStart,
      label: 'Insertar fila arriba',
      disabled: !editor.can().addRowBefore(),
      onClick: () => runCommand(() => editor.chain().focus().addRowBefore().run()),
    },
    {
      key: 'add-row',
      icon: BetweenHorizontalEnd,
      label: 'Insertar fila abajo',
      disabled: !editor.can().addRowAfter(),
      onClick: () => runCommand(() => editor.chain().focus().addRowAfter().run()),
    },
    {
      key: 'delete-row',
      icon: Trash2,
      label: 'Eliminar fila',
      disabled: !editor.can().deleteRow(),
      onClick: () => runCommand(() => editor.chain().focus().deleteRow().run()),
    },
    { key: 'separator-2', separator: true },
    // Combinar / dividir celdas
    {
      key: 'merge-cells',
      icon: TableCellsMerge,
      label: 'Combinar celdas',
      disabled: !editor.can().mergeCells(),
      onClick: () => runCommand(() => editor.chain().focus().mergeCells().run()),
    },
    {
      key: 'split-cell',
      icon: TableCellsSplit,
      label: 'Dividir celda',
      disabled: !editor.can().splitCell(),
      onClick: () => runCommand(() => editor.chain().focus().splitCell().run()),
    },
    { key: 'separator-3', separator: true },
    // Seleccionar fila / columna / tabla
    {
      key: 'select-row',
      icon: Rows3,
      label: 'Seleccionar fila',
      onClick: () => runCommand(selectRow),
    },
    {
      key: 'select-column',
      icon: Columns3,
      label: 'Seleccionar columna',
      onClick: () => runCommand(selectColumn),
    },
    {
      key: 'select-table',
      icon: Table,
      label: 'Seleccionar tabla',
      onClick: () => runCommand(selectWholeTable),
    },
    { key: 'separator-4', separator: true },
    // Enviar al chat
    {
      key: 'send-to-chat',
      icon: Send,
      label: 'Enviar al chat',
      onClick: () => runCommand(sendTableToChat),
    },
    { key: 'separator-5', separator: true },
    // Eliminar tabla completa
    {
      key: 'delete-table',
      icon: Trash2,
      label: 'Eliminar tabla',
      disabled: !editor.can().deleteTable(),
      onClick: () => runCommand(() => editor.chain().focus().deleteTable().run()),
    },
  ];

  // Whole-table comment button, top-right corner of the table. Amber with a +
  // when the table is commented (opens its thread); white when not (selects the
  // whole table and starts a new comment).
  const COMMENT_BTN = 30;
  const commented = position.commentId != null;
  const commentTop = Math.max((position.tableTop ?? position.top) - COMMENT_BTN - 4, 0);
  const commentLeft = Math.max((position.tableRight ?? position.left) - COMMENT_BTN, 0);

  const onCommentClick = () => {
    if (position.commentId != null) {
      window.dispatchEvent(new CustomEvent('openCommentThread', { detail: { commentId: position.commentId } }));
      return;
    }
    selectWholeTable();
    window.dispatchEvent(new CustomEvent('openCommentsAndCompose'));
  };

  return (
    <>
      <div
        ref={toolbarRef}
        className="table-floating-toolbar"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
        onMouseDown={handleMouseDown}
      >
        {buttons.map((button) => {
          if (button.separator) {
            return <div key={button.key} className="separator" />;
          }

          const Icon = button.icon;

          return (
            <button
              key={button.key}
              type="button"
              title={button.label}
              aria-label={button.label}
              disabled={button.disabled}
              onClick={button.onClick}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="table-comment-button"
        title={commented ? 'Ver comentario de la tabla' : 'Comentar toda la tabla'}
        aria-label={commented ? 'Ver comentario de la tabla' : 'Comentar toda la tabla'}
        onMouseDown={handleMouseDown}
        onClick={onCommentClick}
        style={{
          position: 'absolute',
          top: `${commentTop}px`,
          left: `${commentLeft}px`,
          width: `${COMMENT_BTN}px`,
          height: `${COMMENT_BTN}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: commented ? '#f59e0b' : '#ffffff',
          color: commented ? '#ffffff' : '#f59e0b',
          border: `1px solid ${commented ? '#d97706' : '#fcd34d'}`,
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer',
          zIndex: 40,
        }}
      >
        <MessageSquarePlus className="w-5 h-5" />
      </button>
    </>
  );
};

export default TableFloatingToolbar;